import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, MapPin, Clock } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { FaqAccordion, buildFaqLd } from "../components/FaqAccordion";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  attractions: "Attractions & Landmarks",
  towns: "Nearby Towns",
  highways: "Highways & Corridors",
};

export function ExploreIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/explore`).then((r) => { if (alive) setItems(r.data || []); });
    return () => { alive = false; };
  }, []);
  const grouped = useMemo(() => {
    const g = { attractions: [], towns: [], highways: [] };
    items.forEach((i) => { if (g[i.category]) g[i.category].push(i); });
    return g;
  }, [items]);
  const canonical = "https://westbridgehotels.com/explore";
  useSeo({
    title: "Explore Local — Rathbun Lake, Historic Square & More · Westbridge Inn",
    description: "Where to stay near Rathbun Lake, the Centerville historic square, MercyOne, Indian Hills, Sharon Bluffs, and southern Iowa's small towns.",
    canonical,
    jsonLd: [buildBreadcrumbLd([{ label: "Explore Local", to: "/explore" }])],
  });
  return (
    <main id="main" data-testid="explore-index-page">
      <PageHero
        eyebrow="Explore Local"
        title="Southern Iowa, from our front door."
        intro="Rathbun Lake at seven miles. The Centerville historic square at two blocks. MercyOne, Indian Hills, Sharon Bluffs, and every small town on Hwy 2 within a short drive."
        breadcrumbs={[{ label: "Explore Local", to: "/explore" }]}
        testId="explore-index-hero"
        compact
      />
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app space-y-16">
          {["attractions","towns","highways"].map((cat) => (
            grouped[cat]?.length > 0 && (
              <div key={cat} data-testid={`explore-group-${cat}`}>
                <p className="eyebrow mb-6">{CATEGORY_LABELS[cat]}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {grouped[cat].map((it) => (
                    <Link
                      key={it.slug}
                      to={`/explore/${it.slug}`}
                      className="group bg-cream-50 border border-tan p-6 hover:border-brass/60 transition-colors"
                      data-testid={`explore-card-${it.slug}`}
                    >
                      <p className="eyebrow text-brass mb-2 flex items-center gap-2"><MapPin size={12} /> {it.distance}</p>
                      <h3 className="font-display text-xl text-forest-900 mb-2 group-hover:text-brass">{it.name}</h3>
                      <p className="text-sm text-ink/75 leading-relaxed line-clamp-3">{it.what_it_is}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      </section>
      <CtaBlock eyebrow="Base yourself here" title="One quiet base for a weekend across southern Iowa." />
    </main>
  );
}

export function ExploreDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    let alive = true;
    setItem(null); setNotFound(false);
    (async () => {
      try {
        const [e, r] = await Promise.all([
          axios.get(`${API}/explore/${slug}`).then((x) => x.data).catch(() => null),
          axios.get(`${API}/rooms`).then((x) => x.data),
        ]);
        if (!alive) return;
        if (!e) { setNotFound(true); return; }
        setItem(e); setRooms(r || []);
      } catch { setNotFound(true); }
    })();
    return () => { alive = false; };
  }, [slug]);
  const relRooms = useMemo(() => item ? (item.recommended_rooms || []).map((s) => rooms.find((r) => r.slug === s)).filter(Boolean) : [], [item, rooms]);
  const canonical = item ? `https://westbridgehotels.com/explore/${item.slug}` : undefined;
  useSeo({
    title: item?.meta_title || "Explore Near Westbridge",
    description: item?.meta_description || "",
    canonical,
    jsonLd: item ? [
      buildBreadcrumbLd([{ label: "Explore Local", to: "/explore" }, { label: item.name, to: `/explore/${item.slug}` }]),
      buildFaqLd(item.faqs),
    ] : [],
  });
  if (notFound) return (<main id="main" className="pt-40 pb-24 min-h-[60vh] bg-cream"><div className="container-app text-center"><p className="eyebrow text-brass mb-3">404</p><h1 className="font-display text-4xl text-forest-900">Explore item not found</h1><Link to="/explore" className="btn-ghost-dark mt-6 inline-block">Back to Explore</Link></div></main>);
  if (!item) return (<main id="main" className="pt-40 pb-24 min-h-[60vh] bg-cream"><div className="container-app"><div className="w-full h-72 bg-tan-200 animate-pulse" /></div></main>);
  return (
    <main id="main" data-testid="explore-detail">
      <PageHero
        eyebrow={CATEGORY_LABELS[item.category] || "Explore Local"}
        title={item.name}
        intro={item.what_it_is}
        breadcrumbs={[{ label: "Explore Local", to: "/explore" }, { label: item.name, to: `/explore/${item.slug}` }]}
        testId="explore-detail-hero"
      />
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7 max-w-2xl">
            <div className="flex flex-wrap gap-6 mb-8">
              <span className="inline-flex items-center gap-2 text-sm text-ink/70"><MapPin size={16} className="text-brass" /> {item.distance}</span>
              <span className="inline-flex items-center gap-2 text-sm text-ink/70"><Clock size={16} className="text-brass" /> {item.drive_time}</span>
            </div>
            {item.what_to_do && (<><h2 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">What to do</h2><p className="text-ink/85 leading-relaxed mb-8">{item.what_to_do}</p></>)}
            {item.eat_hint && (<><h2 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">Where to eat</h2><p className="text-ink/85 leading-relaxed mb-8">{item.eat_hint}</p></>)}
            <h2 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">Stay at Westbridge</h2>
            <p className="text-ink/85 leading-relaxed">{item.westbridge_pitch}</p>
          </div>
          <aside className="md:col-span-5">
            {item.outbound_links?.length > 0 && (
              <div className="bg-cream-100 border border-tan p-6 md:p-8 mb-6" data-testid="explore-outbound-links">
                <p className="eyebrow mb-4">Learn more</p>
                <ul className="space-y-3 text-sm">
                  {item.outbound_links.map((l, i) => (
                    <li key={i}>
                      <a href={l.href} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 text-forest-900 hover:text-brass">
                        {l.label} <ExternalLink size={12} />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {relRooms.length > 0 && (
              <div className="bg-cream-100 border border-tan p-6 md:p-8">
                <p className="eyebrow mb-4">Recommended rooms</p>
                <ul className="space-y-2">
                  {relRooms.map((r) => (<li key={r.slug}><Link to={`/rooms/${r.slug}`} className="text-forest-900 hover:text-brass" data-testid={`explore-related-room-${r.slug}`}>{r.name} →</Link></li>))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>
      {item.faqs?.length > 0 && (
        <section className="bg-cream-100 py-16 md:py-24 paper-grain" data-testid="explore-faq">
          <div className="container-app relative grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4"><p className="eyebrow mb-3">Common questions</p><h2 className="font-display font-light text-2xl md:text-4xl text-forest-900 tracking-tight">About {item.name}.</h2></div>
            <div className="md:col-span-8"><FaqAccordion items={item.faqs} testIdPrefix="explore-faq" /></div>
          </div>
        </section>
      )}
      <CtaBlock />
    </main>
  );
}
